import debug from 'debug'
import { renderPage } from '../../domain/address/address.page'
import { CMS_ADDRESS_PAGES } from '../../domain/cms/cms.constants'
import AddressNotFoundError from '../../domain/error/AddressNotFoundError'
import PageNotFoundError from '../../domain/error/PageNotFoundError'
import { bboxArrayToBboxArrays } from '../../domain/map/conversion'
import { POPUP_TYPE_ADDRESS } from '../../domain/map/popup/popup.selectors'
import { extractQueryStringParamsFromUrl } from '../../domain/router/routeUtils'
import { hasHtmlExtension } from '../../domain/seo/seo'
import { extractZipCity, isZipDashCity } from '../../domain/utils/location'
import { fetchBrowserCmsAddressPageContent, searchAddress, searchAddressRaw } from '../../store/address/address.actions'
import { isLandmark, isNamedPlace } from '../../store/address/address.helpers'
import {
  selectFirstAddress,
  selectIsAddressWithStreetNumber,
  selectIsAdressInFrance
} from '../../store/address/address.selectors'
import { resetAddress } from '../../store/address/addressSlice'
import { searchGeoentitiesAtAddress } from '../../store/geoentity/geoentity.actions'
import { resetGeoentityList } from '../../store/geoentity/geoentitySlice'
import {
  selectIsAddressPage,
  selectIsAGeoentityPage,
  selectIsASearchPage,
  selectIsGeoentityAtAddressPage,
  selectIsPlacesUniverse,
  selectReqUrl
} from '../../store/history/history.selectors'
import { displayMapPopup, hideMapPopup, mapUpdated } from '../../store/map/mapSlice'
import { selectIsFrenchSite, selectIsOnboardingContext } from '../../store/navigation/navigation.selectors'
import {
  loadCompleteGeoentitiesAtAddressConfiguration,
  loadPoiAssetsConfiguration
} from '../../store/poiassets/poiassets.actions'
import { geocodeForwardCtx, landmarkSubtypes } from '../../store/search/search.service.constants'
import { setSearchTerms } from '../../store/search/searchSlice'
import { searchSeoCitiesForFooter, searchSeoCityData } from '../../store/seo/seo-address.actions'
import {
  redirectCityRouteIfDifferentPostcode,
  redirectCityRouteIfTownUpperCased,
  redirectIfAmp,
  redirectIfStartsWithDash,
  redirectToLandmark,
  redirectToNamedPlace
} from '../../store/seo/seo-city.actions'
import { fetchServerCmsAddressPageContent } from '../../store/seo/seo-cms.actions'
import { loadSeoActivitiesForCity, loadSeoPoisForCity, loadTopAppsForTown } from '../../store/seo/seo-poi.actions'
import { setBlockCanonical } from '../../store/seo/seoSlice'
import { ROUTE_SEO_ADDRESS, ROUTE_SEO_LANDMARK, ROUTE_SEO_NAMED_PLACE } from '../index'
import { technicalErrorCatcher } from '../routeErrors.utils'
import { handleRouteLoading } from '../routeService'

import addressPageQuery from '../../domain/cms/address-pages.query.json'

const d = debug('addressRoute')

const UNKNOWN_ROUTE = 'unknown'

const geographicTypeByRouteName = {
  [ROUTE_SEO_NAMED_PLACE]: geocodeForwardCtx.filter.hamlet,
  [ROUTE_SEO_LANDMARK]: geocodeForwardCtx.filter.landmark,
  [ROUTE_SEO_ADDRESS]: undefined
}
const geographicSubTypesByRouteName = {
  [ROUTE_SEO_NAMED_PLACE]: undefined,
  [ROUTE_SEO_LANDMARK]: landmarkSubtypes,
  [ROUTE_SEO_ADDRESS]: undefined
}

const getRouteNameByContext = routeContext => {
  const parts = routeContext.path.split('/')
  if (parts.length === 4) {
    if (parts[2] === 'lieu-dit') return ROUTE_SEO_NAMED_PLACE
    if (parts[2] === 'lieu') return ROUTE_SEO_LANDMARK
    return UNKNOWN_ROUTE
  }
  return ROUTE_SEO_ADDRESS
}

export function onRouteEnter({ params, store: { dispatch, getState }, avoidRefetchingPageData, routeContext }) {
  d('onRouteEnter')
  const { location } = params
  const routeName = getRouteNameByContext(routeContext)

  const landMarkOrNamedPlaceAndNotFrenchSite = routeName !== ROUTE_SEO_ADDRESS && !selectIsFrenchSite(getState())
  if (routeName === UNKNOWN_ROUTE || landMarkOrNamedPlaceAndNotFrenchSite) {
    throw new PageNotFoundError()
  }
  const reqUrl = selectReqUrl(getState())
  const queryStringParams = extractQueryStringParamsFromUrl(reqUrl)

  if (__SERVER__) {
    if (hasHtmlExtension(location)) throw new PageNotFoundError()
    redirectIfAmp(location, queryStringParams)
    redirectIfStartsWithDash(location, queryStringParams)
    redirectCityRouteIfTownUpperCased(location, routeName, queryStringParams)
  }

  if (selectIsOnboardingContext(getState()) || routeContext?.action === 'POP') {
    dispatch(setSearchTerms(location))
  }

  return Promise.resolve()
    .then(() => {
      if (avoidRefetchingPageData) return Promise.resolve()
      return searchAddress({
        terms: location,
        options: {
          avoidRedirect: true,
          geographicType: geographicTypeByRouteName[routeName],
          subtypes: geographicSubTypesByRouteName[routeName]
        }
      })(dispatch, getState)
    })
    .then(() => {
      if (__SERVER__) {
        redirectCityRouteIfDifferentPostcode(location, routeName, queryStringParams)(dispatch, getState)
        redirectToNamedPlace(location, routeName, queryStringParams)(dispatch, getState)
        const address = selectFirstAddress(getState())
        if (!selectIsAdressInFrance(getState())) {
          dispatch(setBlockCanonical(!isZipDashCity(location) || isLandmark(address) || isNamedPlace(address)))
        }
        if (!selectIsFrenchSite(getState())) {
          dispatch(setBlockCanonical(isLandmark(address) || isNamedPlace(address)))
        }

        if (isLandmark(selectFirstAddress(getState()))) {
          return searchAddressRaw({
            terms: location,
            options: {
              avoidRedirect: true,
              geographicType: geographicTypeByRouteName[ROUTE_SEO_LANDMARK],
              subtypes: geographicSubTypesByRouteName[ROUTE_SEO_LANDMARK]
            }
          })(dispatch, getState)
            .then(({ addresses } = {}) => {
              if (addresses.length) {
                // landmark found, redirect if needed
                redirectToLandmark(location, routeName, queryStringParams, addresses[0])(dispatch, getState)
              }
            })
            .catch(e => {
              if (d.enabled) d('landmark second search error', e)
              if (e instanceof AddressNotFoundError) {
                // no landmark found, so do NOT redirect
                dispatch(setBlockCanonical(true))
                return
              }
              throw e
            })
        }
      }
    })
    .then(() => {
      if (__BROWSER__) {
        return fetchBrowserCmsAddressPageContent()(dispatch, getState)
      } else {
        const firstAddress = selectFirstAddress(getState())
        dispatch(mapUpdated({ bbox: bboxArrayToBboxArrays(firstAddress?.bbox) }))
        const { postcode } = extractZipCity(location)

        return Promise.all([
          selectIsAddressWithStreetNumber(getState()) && searchGeoentitiesAtAddress(location)(dispatch, getState),
          selectIsFrenchSite(getState()) &&
            Promise.all([
              loadPoiAssetsConfiguration()(dispatch, getState),
              loadCompleteGeoentitiesAtAddressConfiguration()(dispatch, getState)
            ]),
          postcode &&
            Promise.all([
              fetchServerCmsAddressPageContent(CMS_ADDRESS_PAGES, addressPageQuery)(dispatch, getState),
              searchSeoCityData()(dispatch, getState).then(() =>
                Promise.all([
                  loadTopAppsForTown(postcode, firstAddress.towncode, 31)(dispatch, getState),
                  searchSeoCitiesForFooter()(dispatch, getState),
                  loadSeoActivitiesForCity()(dispatch, getState),
                  loadSeoPoisForCity()(dispatch, getState)
                ])
              )
            ])
        ])
      }
    })
    .then(() => {
      dispatch(displayMapPopup(POPUP_TYPE_ADDRESS))
    })
    .catch(technicalErrorCatcher)
}

export function onRouteLeave({ routeContext }) {
  const {
    store: { dispatch, getState }
  } = routeContext

  if (!selectIsPlacesUniverse(getState())) {
    dispatch(resetAddress())
  }
  if (
    !selectIsGeoentityAtAddressPage(getState()) &&
    !selectIsASearchPage(getState()) &&
    !selectIsAGeoentityPage(getState()) &&
    !selectIsAddressPage(getState())
  ) {
    dispatch(resetGeoentityList())
  }
  dispatch(hideMapPopup())
}

export default handleRouteLoading({ onRouteEnter, renderPage, onRouteLeave })
